import { type Monitor, Signal, batch } from "@suse-o11y/signal";
import type { GetFormInput, GetFormOutput } from "@suse-o11y/signal/form";
import { z } from "zod";

import { isBefore } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";
import type { IdOrIdentifier } from "~/tools/id-or-identifier";
import { isShallowEqual } from "~/tools/is-shallow-equal";
import { LocalStorage, UNSAVED_DASHBOARD_PREFIX } from "~/tools/local-storage";

import { DashboardSpec } from "../../../common/dashboard-spec";

import { type DashboardFormState, dashboardFormOutputToSpec, initDashboardFormState } from "./_dashboard-form-state";

function getLocalStorageKey(idOrIdentifier: IdOrIdentifier): string {
	return `${UNSAVED_DASHBOARD_PREFIX}${idOrIdentifier}`;
}

/**
 * The snapshot provides necessary information to (re)populate the dashboard form state.
 */
export interface DashboardStateSnapshot {
	idOrIdentifier: IdOrIdentifier;
	lastUpdateTimestamp: Time;
	spec: DashboardSpec;
}

/**
 * The compare function to determine if the form state should be (re)populated.
 */
const shouldRepopulate = (current: undefined | DashboardStateSnapshot, next: DashboardStateSnapshot) => {
	return (
		// repopulate when switching to a different dashboard
		current?.idOrIdentifier !== next.idOrIdentifier ||
		// repopulate when the next snapshot is newer and spec has changed
		(isBefore(current.lastUpdateTimestamp, next.lastUpdateTimestamp) && !current.spec.isEqual(next.spec))
	);
};

/**
 * The function to create the form input ensuring all fields are set.
 */
function specToInput({
	panels,
	variables,
	refreshInterval = null,
	timeRange = null,
	saveVariables,
}: DashboardSpec): GetFormInput<DashboardFormState> {
	return {
		panels,
		variables,
		settings: { refreshInterval, timeRange, saveVariables },
	};
}

/**
 * The dashboard state managing the form state and its persistence.
 * It keeps internal snapshot to determine when to (re)populate the form state.
 */
export class DashboardState {
	private readonly snapshot = Signal<undefined | DashboardStateSnapshot>(undefined, {
		equals: isShallowEqual,
	});

	public readonly form = initDashboardFormState();

	/**
	 * Updates the dashboard state from the provided snapshot, resetting or initializing
	 * the form based on whether the new snapshot differs from the current one.
	 *
	 * @param snapshot - The snapshot whose data should populate the state and form.
	 */
	public populate(snapshot: DashboardStateSnapshot): void {
		batch((monitor) => {
			const input = specToInput(snapshot.spec);

			if (shouldRepopulate(this.snapshot.read(monitor), snapshot)) {
				this.snapshot.write(snapshot);
				// completely reset the form to match the new snapshot
				this.form.reset(input);
			} else {
				// change the initial input but keep the current changes
				this.form.setInitial(input);
			}
		});
	}

	/**
	 * Clears the dashboard state only if the provided identifier matches the current snapshot.
	 *
	 * @param idOrIdentifier - The unique identifier of the dashboard whose state should be reset.
	 *
	 * @remarks Ensures state resets do not affect other dashboards when navigating between them.
	 */
	public clear(idOrIdentifier: IdOrIdentifier): void {
		batch((monitor) => {
			if (this.snapshot.read(monitor)?.idOrIdentifier === idOrIdentifier) {
				this.snapshot.write(undefined);
				this.form.reset();
			}
		});
	}

	/**
	 * Attempts to create a saver for the current dashboard state.
	 *
	 * @param monitor
	 *
	 * @returns A dashboard state saver when state is available; otherwise, `undefined`.
	 */
	public toSaver(monitor: Monitor): undefined | DashboardStateSaver {
		const state = this.snapshot.read(monitor);

		if (state == null) {
			return undefined;
		}

		const key = getLocalStorageKey(state.idOrIdentifier);
		const output = this.form.getOutput(monitor);

		return new DashboardStateSaver(key, output);
	}

	/**
	 * Attempts to create a loader for a dashboard state.
	 *
	 * @param monitor
	 *
	 * @returns A initialized `DashboardStateLoader` when a stale revision exists, or `undefined` if none should be loaded.
	 */
	public toLoader(monitor: Monitor): undefined | DashboardStateLoader {
		const state = this.snapshot.read(monitor);

		if (state == null) {
			return undefined;
		}

		const revision = LocalStorage.getItem(
			monitor,
			getLocalStorageKey(state.idOrIdentifier),
			z.tuple([
				// whether to autoload the saved changes
				z.boolean(),
				DashboardSpec.schema,
			]),
		);

		if (revision == null) {
			return undefined;
		}

		const output = this.form.getOutput(monitor);

		if (output != null && dashboardFormOutputToSpec(output).isEqual(revision[1])) {
			return undefined;
		}

		return new DashboardStateLoader(revision[0], revision[1], this.form);
	}
}

/**
 * Manages persisting the current dashboard form state to local storage and removing it when unavailable.
 *
 * @remarks
 * The saved state includes whether the dashboard should autoload.
 * The class is a wrapper around the `DashboardState.form` output, so it guarantees that async saves are properly handled.
 */
export class DashboardStateSaver {
	public constructor(
		public readonly key: string,
		private readonly output: null | GetFormOutput<DashboardFormState>,
	) {}

	/**
	 * Persists or clears the dashboard state in local storage based on the current output value.
	 *
	 * @param options.autoload - Whether the dashboard should autoload when retrieved from storage. Default is `false`.
	 */
	public save({ autoload }: { autoload: boolean } = { autoload: false }): void {
		if (this.output == null) {
			this.remove();
		} else {
			LocalStorage.setItem(this.key, [autoload, dashboardFormOutputToSpec(this.output).toPerses()]);
		}
	}

	/**
	 * Removes the stored dashboard state associated with this instance from local storage.
	 */
	public remove(): void {
		LocalStorage.removeItem(this.key);
	}
}

/**s
 * Orchestrates loading a dashboard specification into the form state, respecting the auto-loading preferences.
 *
 * @remarks
 * The loader delegates conversion of the dashboard specification to a form input representation before updating the form state.
 * The class is a wrapper around the `DashboardSpec` which should be loaded, so it guarantees that async loads are properly handled.
 */
export class DashboardStateLoader {
	public constructor(
		public readonly autoload: boolean,
		private readonly spec: DashboardSpec,
		private readonly formState: DashboardFormState,
	) {}

	/**
	 * Loads the dashboard data into the form state when invoked.
	 */
	public load(): void {
		this.formState.setInput(specToInput(this.spec));
	}
}
